import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const [shippingCost, setShippingCost] = useState(10); // Default shipping cost
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const { settings, loading: settingsLoading } = useSettings();
  const { isDarkMode } = useTheme();

  // Currency formatting helper
  const formatCurrency = (amount) => {
    if (settingsLoading) return `$${amount.toFixed(2)}`;
    const currency = settings.payment?.currency || 'USD';
    const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
    return `${symbol}${amount.toFixed(2)}`;
  };

  // Get subtotal from cart context
  const subtotal = getCartTotal();

  // Handle quantity change for an item
  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(itemId, newQuantity);
  };

  // Remove an item from cart
  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
  };

  // Apply coupon code
  const handleApplyCoupon = () => {
    // In a real app, you would verify this with an API
    if (couponCode.toLowerCase() === 'tafsir10') {
      const discountAmount = subtotal * 0.1; // 10% discount
      setDiscount(discountAmount);
    } else {
      alert('Invalid or expired coupon code');
      setDiscount(0);
    }
  };

  // Calculate final total
  const total = subtotal + shippingCost - discount;

  // If cart is empty
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--background)] transition-colors duration-300 pt-24 md:pt-28 pb-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-8">Your Cart</h1>
          
          <div className="bg-[var(--surface)] text-[var(--text-primary)] rounded-islamic p-8 shadow-md text-center border border-[var(--border)]">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-semibold text-[var(--text-primary)] mb-4">Your cart is empty</h2>
            <p className="text-[var(--text-secondary)] mb-6">Looks like you haven't added any artworks to your cart yet.</p>
            <Link to="/gallery" className="btn-primary inline-block">
              Browse Gallery
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] transition-colors duration-300 pt-24 md:pt-28 pb-16">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-8">Your Cart</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - Left Column */}
          <div className="lg:col-span-2">
            <div className="bg-[var(--surface)] text-[var(--text-primary)] rounded-islamic shadow-md overflow-hidden border border-[var(--border)]">
              <div className="px-6 py-4 bg-[var(--surface-elevated)] border-b border-[var(--border)]">
                <h2 className="font-semibold text-xl text-[var(--text-primary)]">Cart Items ({cart.length})</h2>
              </div>
              
              <ul className="divide-y divide-[var(--border)]">
                {cart.map((item, index) => (
                  <li key={item._id} className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 rounded-islamic overflow-hidden flex-shrink-0 border border-[var(--border)]">
                        <img 
                          src={item.imageUrl} 
                          alt={item.title} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Product Info */}
                      <div className="flex-grow">
                        <h3 className="font-semibold text-[var(--primary)]">{item.title}</h3>
                        {item.variant && (
                          <p className="text-[var(--text-secondary)] text-sm mb-2">Variant: {item.variant.title}</p>
                        )}
                        <p className="text-[var(--accent)] font-medium">{formatCurrency(item.price)}</p>
                        
                        <div className="flex justify-between items-center mt-4">
                          <div className="flex items-center">
                            <button 
                              onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center border border-[var(--border)] rounded-l-sm bg-[var(--surface-elevated)] hover:bg-[var(--surface)] transition-colors"
                            >
                              -
                            </button>
                            <input 
                              type="number" 
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(item._id, parseInt(e.target.value) || 1)}
                              className="w-12 h-8 border-t border-b border-[var(--border)] text-center bg-[var(--surface)] text-[var(--text-primary)]"
                            />
                            <button 
                              onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center border border-[var(--border)] rounded-r-sm bg-[var(--surface-elevated)] hover:bg-[var(--surface)] transition-colors"
                            >
                              +
                            </button>
                          </div>
                          
                          <button 
                            onClick={() => handleRemoveItem(item._id)}
                            className="text-sm text-red-500 hover:text-red-700 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      
                      {/* Price Total */}
                      <div className="text-right sm:w-24">
                        <span className="font-semibold text-[var(--text-primary)]">{formatCurrency(item.price * item.quantity)}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              
              <div className="p-6 bg-[var(--surface-elevated)] border-t border-[var(--border)]">
                <Link to="/store" className="text-[var(--primary)] hover:text-[var(--primary-dark)] transition-colors">
                  ← Continue Shopping
                </Link>
              </div>
            </div>
          </div>
          
          {/* Order Summary - Right Column */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--surface)] text-[var(--text-primary)] rounded-islamic shadow-md p-6 border border-[var(--border)]">
              <h2 className="font-semibold text-xl text-[var(--text-primary)] mb-4">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Subtotal</span>
                  <span className="font-medium text-[var(--text-primary)]">{formatCurrency(subtotal)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Shipping</span>
                  <span className="font-medium text-[var(--text-primary)]">{formatCurrency(shippingCost)}</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                )}
                
                <div className="border-t border-[var(--border)] pt-4 flex justify-between">
                  <span className="font-semibold text-[var(--text-primary)]">Total</span>
                  <span className="font-bold text-[var(--primary)]">{formatCurrency(total)}</span>
                </div>
              </div>
              
              {/* Coupon Code */}
              <div className="mb-6">
                <label htmlFor="coupon" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                  Coupon Code
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="coupon"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-l-islamic focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                    placeholder="Enter code"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    className="bg-[var(--accent)] text-white px-4 py-2 rounded-r-islamic hover:opacity-90 transition-opacity"
                  >
                    Apply
                  </button>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">Try 'TAFSIR10' for 10% off</p>
              </div>
              
              {/* Checkout Button */}
              <Link
                to="/checkout"
                className="btn-primary w-full py-3 text-center block"
              >
                Proceed to Checkout
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;